import React from 'react';
import { SkeletonBodyText, SkeletonDisplayText } from '@shopify/polaris';
import { useTranslation } from '../../i18n';

/**
 * Loading skeletons that match the final layout — used instead of bare
 * spinners while a page's data loads.
 *
 *   <PageSkeleton variant="form" />      long single-column form
 *   <PageSkeleton variant="grid" />      header + card grid (products, images)
 *   <PageSkeleton variant="detail" />    two-column detail (preview + sidebar)
 *   <PageSkeleton variant="list" />      header + rows (tables)
 *   <PageSkeleton variant="chat" />      chat thread + composer
 *   <PageSkeleton variant="dashboard" /> stat row + cards
 *
 * `title`/`description` render a real page header so the title doesn't
 * jump when the content arrives. `label` is the accessible status text.
 */
export default function PageSkeleton({ variant = 'form', title, description, label, rows = 3, testId = 'page-skeleton' }) {
  const { t } = useTranslation();
  return (
    <div className="au-skel" role="status" aria-busy="true" aria-label={label || t('common.loading')} data-testid={testId}>
      {(title || description) ? (
        <div className="au-page-header">
          <div className="au-page-header__text">
            {title && <h1 className="au-page-header__title">{title}</h1>}
            {description && <p className="au-page-header__desc">{description}</p>}
          </div>
        </div>
      ) : (
        <div className="au-skel__header"><SkeletonDisplayText size="medium" /><div style={{ maxWidth: 420, marginTop: 8 }}><SkeletonBodyText lines={1} /></div></div>
      )}

      {variant === 'form' && (
        <div className="au-skel__stack">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="au-skel__card"><SkeletonDisplayText size="small" /><div style={{ marginTop: 12 }}><SkeletonBodyText lines={3} /></div></div>
          ))}
        </div>
      )}

      {variant === 'grid' && (
        <div className="au-skel__grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="au-skel__card"><div className="au-skel__thumb" /><div style={{ marginTop: 10 }}><SkeletonBodyText lines={2} /></div></div>
          ))}
        </div>
      )}

      {variant === 'detail' && (
        <div className="au-skel__detail">
          <div className="au-skel__card au-skel__card--tall"><div className="au-skel__thumb au-skel__thumb--tall" /></div>
          <div className="au-skel__stack">
            <div className="au-skel__card"><SkeletonDisplayText size="small" /><div style={{ marginTop: 12 }}><SkeletonBodyText lines={4} /></div></div>
            <div className="au-skel__card"><SkeletonBodyText lines={3} /></div>
          </div>
        </div>
      )}

      {variant === 'list' && (
        <div className="au-skel__card">
          {Array.from({ length: Math.max(rows, 4) }).map((_, i) => (
            <div key={i} className="au-skel__row"><div className="au-skel__thumb au-skel__thumb--sm" /><div style={{ flex: 1 }}><SkeletonBodyText lines={1} /></div><div style={{ width: 90 }}><SkeletonBodyText lines={1} /></div></div>
          ))}
        </div>
      )}

      {variant === 'chat' && (
        <div className="au-skel__chat">
          <div className="au-skel__card au-skel__card--tall"><SkeletonBodyText lines={2} /><div style={{ height: 24 }} /><SkeletonBodyText lines={3} /><div style={{ height: 24 }} /><SkeletonBodyText lines={2} /></div>
          <div className="au-skel__card"><SkeletonBodyText lines={1} /></div>
        </div>
      )}

      {variant === 'dashboard' && (
        <>
          <div className="au-skel__stats">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="au-skel__card"><SkeletonBodyText lines={2} /></div>)}
          </div>
          <div className="au-skel__grid au-skel__grid--3">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="au-skel__card"><SkeletonDisplayText size="small" /><div style={{ marginTop: 12 }}><SkeletonBodyText lines={2} /></div></div>)}
          </div>
        </>
      )}
    </div>
  );
}
